import React, { useState } from 'react';
import { Bot, User, Copy, Check, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { TypingIndicator } from './TypingIndicator';
import { MultiAgentTracker } from './MultiAgentTracker';

function renderInlineMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function preprocessMarkdown(text = '') {
  if (!text) return '';
  let res = text;

  // 1. Break inline bullets into newlines when preceded by punctuation
  res = res.replace(/:\s*([*•-])\s+/g, ':\n* ');
  res = res.replace(/([.!?])\s+([*•-])\s+/g, '$1\n* ');

  // 2. Identify explicit contact channel labels followed by a mandatory colon (e.g., "Phone:", "**Email:**")
  const contactLabels = [
    'Phone\\s*(?:[\\/\\&]\\s*WhatsApp)?',
    'WhatsApp',
    'Email',
    'Visit\\s*Us',
    'Address'
  ];

  const contactPattern = contactLabels.join('|');
  // Require a colon either inside or immediately outside bold markers so prose words like "Email" are never split
  const inlineContactRegex = new RegExp(
    '(.+?)[ \\t]+(?:[*•|-][ \\t]*)?(?:[^\\w\\s*]+[ \\t]*)?(?:\\*\\*(' + contactPattern + '):\\*\\*|\\*\\*(' + contactPattern + ')\\*\\*:|(' + contactPattern + '):)[ \\t]*',
    'gi'
  );

  res = res
    .split('\n')
    .map((line) =>
      line.replace(inlineContactRegex, (match, prefix, l1, l2, l3) => {
        // If prefix is only bullet markers, spaces, or emojis (e.g. "* 📞"), keep the bullet intact
        if (!/[a-zA-Z0-9]{2}/.test(prefix)) {
          return match;
        }
        const label = (l1 || l2 || l3 || '').trim();
        return `${prefix.trim()}\n* **${label}:** `;
      })
    )
    .join('\n');

  return res.trim();
}

function SimpleMarkdown({ children }) {
  const content = typeof children === 'string' ? children : String(children || '');
  const processed = preprocessMarkdown(content);
  const lines = processed.split('\n');
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    elements.push(
      <ul key={`list-${elements.length}`}>
        {listItems.map((item, index) => (
          <li key={index}>{renderInlineMarkdown(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    if (/^[-*•]\s+/.test(trimmed)) {
      listItems.push(trimmed.replace(/^[-*•]\s+/, ''));
      return;
    }

    flushList();
    elements.push(<p key={`p-${index}`}>{renderInlineMarkdown(trimmed)}</p>);
  });

  flushList();
  return elements;
}

export function ChatMessage({ message, onSelectQuestion, isLoading }) {
  const isAssistant = message.role === 'assistant';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!message.content) return;
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isGreetingMessage =
    message.id === 'welcome-msg' ||
    message.content?.includes('I am the official AI assistant for Sagveek Technologies') ||
    /^(👋\s*)?hello!/i.test(message.content?.trim() || '');

  const suggestions = isGreetingMessage ? [] : (message.relatedQuestions || []).slice(0, 3);

  return (
    <div className={`message-row ${message.role}`}>
      <div className={`msg-avatar ${message.role}`}>
        {isAssistant ? <Bot size={16} /> : <User size={16} />}
      </div>

      <div className="msg-bubble-wrap">
        <div className="message-bubble">
          {/* Level 3 Multi-Agent State Graph Tracker */}
          {(message.agentEvents?.length > 0 || message.multiAgentWorkflow) && (
            <MultiAgentTracker events={message.agentEvents} workflow={message.multiAgentWorkflow} />
          )}

          {message.content ? (
            <div className="markdown-content">
              <SimpleMarkdown>{message.content}</SimpleMarkdown>
            </div>
          ) : (
            <TypingIndicator />
          )}

          {/* Ticket Confirmation Card */}
          {message.ticket && (
            <div className="ticket-card-inline">
              <div className="ticket-card-top">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle2 size={16} color="#10b981" />
                  <span className="ticket-id-tag">Ticket #{message.ticket.id}</span>
                </div>
                <span className="ticket-status-badge">{message.ticket.status}</span>
              </div>
              <div className="ticket-card-details">
                <div className="ticket-detail-item">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{message.ticket.name}</span>
                </div>
                <div className="ticket-detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">
                    {message.ticket.email} (Email Verified ✓)
                  </span>
                </div>
                {message.ticket.phone && (
                  <div className="ticket-detail-item">
                    <span className="detail-label">Mobile:</span>
                    <span className="detail-value">
                      {message.ticket.phone}
                    </span>
                  </div>
                )}
                <div className="ticket-detail-item">
                  <span className="detail-label">Priority:</span>
                  <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                    {message.ticket.priority}
                  </span>
                </div>
              </div>
            </div>
          )}

          {isAssistant && message.content && !message.isStreaming && (
            <button
              onClick={handleCopy}
              className="copy-btn"
              title="Copy answer"
              style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
            >
              {copied ? <Check size={12} color="#10b981" /> : <Copy size={12} />}
            </button>
          )}
        </div>

        {/* 3 Relevant Suggestions below the assistant answer */}
        {isAssistant && !message.isStreaming && suggestions.length > 0 && (
          <div className="related-questions-wrap">
            <div className="related-questions-header">
              <Sparkles size={12} className="related-icon" />
              <span>Suggested questions</span>
            </div>

            <div className="related-questions-list">
              {suggestions.map((item, index) => {
                const text = typeof item === 'string' ? item : (item.text || String(item));
                const isTicketSuggestion = /raise a ticket/i.test(text);
                return (
                  <button
                    key={index}
                    type="button"
                    className={`related-question-chip ${isTicketSuggestion ? 'ticket-cta-chip' : ''}`}
                    onClick={() => onSelectQuestion && onSelectQuestion(text)}
                    disabled={isLoading}
                    title={isTicketSuggestion ? 'Open Ticket Form (Mobile OTP Verification)' : `Ask: "${text}"`}
                  >
                    <span className="related-question-text">{text}</span>
                    <ArrowRight size={12} className="related-arrow-icon" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {message.timestamp && (
          <span className="msg-timestamp">{message.timestamp}</span>
        )}
      </div>
    </div>
  );
}
